/**
 * AgencyRevenue.jsx — Sprint 5 (Module 08).
 *
 * Aggregate billing across all engagements.  Totals header, per-client breakdown
 * table, and a simple 6-month stacked bar (paid vs. billed).  Read-only;
 * mark-paid still happens inside each engagement's Payments tab.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/api';
import { CurrencyInr, CheckCircle, WarningCircle, Clock } from '@phosphor-icons/react';
import { useCurrency } from '@/hooks/useCurrency';

const ACCENT = '#6366f1';
const PAID = '#10b981';
const DUE = '#f59e0b';
const OVERDUE = '#ef4444';

const AgencyRevenue = () => {
  const { format } = useCurrency();
  // Backend revenue totals are INR-denominated; format() converts to the user's display currency.
  const fmtINR = (n) => format(n, 'INR', { maximumFractionDigits: 0 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/agency/revenue');
        if (!cancelled) setData(r.data);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const maxBar = useMemo(() => Math.max(1, ...((data?.monthly || []).map((m) => Math.max(m.paid, m.billed)))), [data]);

  if (loading) return <div className="p-12 text-sm text-text-muted">Loading…</div>;
  if (!data) return null;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1280px] mx-auto" data-testid="agency-revenue">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: ACCENT }}>Command Centre</div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary leading-[1.05] mb-2" style={{ letterSpacing: '-0.02em' }}>
          Revenue tracker
        </h1>
        <p className="text-sm text-text-secondary">Billings, payments, and dues across every engagement.</p>
      </div>

      {/* Totals header */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat icon={CurrencyInr} label="Total billed" value={fmtINR(data.totals.billed)} accent={ACCENT} testid="revenue-total-billed" />
        <Stat icon={CheckCircle} label="Paid"         value={fmtINR(data.totals.paid)}   accent={PAID}   testid="revenue-total-paid" />
        <Stat icon={Clock}       label="Due"          value={fmtINR(data.totals.due)}    accent={DUE}    testid="revenue-total-due" />
        <Stat icon={WarningCircle} label="Overdue"    value={fmtINR(data.totals.overdue)} accent={OVERDUE} testid="revenue-total-overdue" />
      </div>

      {/* Monthly trend */}
      <div className="bg-surface border border-border rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-text-muted">Last 6 months — paid vs. billed</div>
          <div className="flex items-center gap-3 text-[10px]">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ backgroundColor: PAID }} /> Paid</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ backgroundColor: ACCENT }} /> Billed</span>
          </div>
        </div>
        <div className="flex items-end gap-3 h-40" data-testid="revenue-monthly-chart">
          {(data.monthly || []).map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
              <div className="flex items-end justify-center gap-1 w-full h-32">
                <div className="w-1/2 rounded-t" style={{ height: `${(m.paid / maxBar) * 100}%`, backgroundColor: PAID, minHeight: m.paid ? 4 : 0 }} title={`Paid: ${fmtINR(m.paid)}`} />
                <div className="w-1/2 rounded-t" style={{ height: `${(m.billed / maxBar) * 100}%`, backgroundColor: ACCENT, minHeight: m.billed ? 4 : 0 }} title={`Billed: ${fmtINR(m.billed)}`} />
              </div>
              <div className="text-[10px] font-mono text-text-muted">{m.month}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Per-engagement table */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-text-muted mb-3">Per client</div>
        {data.per_engagement.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg p-10 text-center text-sm text-text-muted">No billings yet.</div>
        ) : (
          <div className="bg-surface border border-border rounded-xl overflow-hidden" data-testid="revenue-per-client-table">
            <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr] gap-3 px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-text-muted border-b border-border bg-background">
              <span>Client</span><span className="text-right">Billed</span><span className="text-right">Paid</span><span className="text-right">Due</span><span className="text-right">Overdue</span>
            </div>
            {data.per_engagement.map((row) => (
              <div key={row.engagement_id} className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr] gap-3 px-4 py-3 border-b border-border last:border-b-0 items-center" data-testid={`revenue-row-${row.engagement_id}`}>
                <div>
                  <div className="text-sm font-medium text-text-primary">{row.founder_label}</div>
                  <div className="text-[10px] text-text-muted capitalize">{row.status}</div>
                </div>
                <div className="text-right font-mono text-sm text-text-primary">{fmtINR(row.billed)}</div>
                <div className="text-right font-mono text-sm" style={{ color: PAID }}>{fmtINR(row.paid)}</div>
                <div className="text-right font-mono text-sm" style={{ color: DUE }}>{fmtINR(row.due)}</div>
                <div className="text-right font-mono text-sm" style={{ color: row.overdue ? OVERDUE : 'inherit' }}>{fmtINR(row.overdue)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="bg-surface border border-border rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-text-muted">{label}</span>
      <span className="w-6 h-6 rounded-md inline-flex items-center justify-center" style={{ backgroundColor: `${accent}1a`, color: accent }}>
        <Icon size={11} weight="bold" />
      </span>
    </div>
    <div className="text-2xl font-mono font-bold text-text-primary">{value}</div>
  </div>
);

export default AgencyRevenue;
